import { create } from "zustand";
import {
  RecordingServer,
  SiteCamera,
} from "../TroubleshootingUtils/troubleshootingApi";
import { safeGetItem, safeSetItem } from "../../../../utils/localStorageUtils";
import {
  AlarmReceiver,
  AlertsReceived,
  CameraLastAlarms,
  SiteCameraTestRun,
  CameraLiveness,
  RefreshStatus,
  ChangesWindow,
  SettingsCompare,
  SiteChanges,
  Finding,
  GapResult,
  PortCheckResponse,
  ReceiverTestRecord,
  ParentAccount,
  ScopeMode,
  SiteConnectionDetail,
  SiteToSiteConnection,
  SmtpInstructions,
  SymptomId,
  TroubleshootingSite,
  TroubleshootingTab,
  WireGuardPeerTelemetry,
  WireGuardSubTab,
  WireGuardTunnel,
  ProbePrefill,
} from "../troubleshootingInterfaces";
import {
  DEFAULT_SAMPLE_SCENARIO,
  SampleScenarioKey,
} from "../TroubleshootingUtils/sampleScenarios";
import { NetworkTarget } from "../TroubleshootingUtils/probeTargets";

/**
 * Page-wide state for Troubleshooting.
 *
 * Every tab reads the same scope (all sites / one site) and the same loaded
 * data, and findings on the Diagnosis tab deep-link into the other tabs
 * (WireGuard debugger, SMTP filename rule, connection check). Keeping tab,
 * scope and cross-tab intents here means a click on a finding can switch tab
 * and preselect a target without prop drilling through five tabs.
 *
 * `sampleData` fills in the surfaces whose backend endpoints do not exist yet
 * with the illustrative data from the design mockup. It is a review aid for
 * the redesign environment, never a substitute for a real response: anything
 * rendered from it carries a "Sample" chip.
 */
interface TroubleshootingState {
  tab: TroubleshootingTab;
  setTab: (tab: TroubleshootingTab) => void;
  wgSubTab: WireGuardSubTab;
  setWgSubTab: (tab: WireGuardSubTab) => void;

  /**
   * Scope is per tab: each tab remembers its own group and site, and gets
   * them back when it is opened again — also after leaving the page, via
   * localStorage. See ScopeMode.
   */
  scope: ScopeMode;
  /** Each tab's last selection; the current tab's is mirrored in scope/groupId/siteId. */
  tabScopes: Partial<Record<TroubleshootingTab, TabScope>>;
  setScope: (scope: ScopeMode) => void;
  /** Parent account the scope is narrowed to; set for "group" and "site". */
  groupId: number | null;
  setGroupId: (groupId: number | null) => void;
  siteId: number | null;
  setSiteId: (siteId: number | null) => void;
  /** Switch tab and land on one site — for cross-tab links that must keep it. */
  openTabForSite: (tab: TroubleshootingTab, siteId: number | null) => void;
  /** Drop loaded data but keep the tab and every tab's selection (page unmount). */
  clearLoadedData: () => void;

  sampleData: boolean;
  setSampleData: (on: boolean) => void;
  /** Which named sample scenario the switch shows (ENG-2039); kept like the switch. */
  sampleScenario: SampleScenarioKey;
  setSampleScenario: (scenario: SampleScenarioKey) => void;

  // ---- loaded data -------------------------------------------------------
  /** Sites in the chosen group only; empty until a group is chosen. */
  sites: TroubleshootingSite[];
  setSites: (sites: TroubleshootingSite[]) => void;
  /** Parent accounts behind those sites, for the group step of the scope picker. */
  parentAccounts: ParentAccount[];
  setParentAccounts: (accounts: ParentAccount[]) => void;
  /** The group list (login cache or group/) is being read. Owned by effect (1). */
  accountsLoading: boolean;
  setAccountsLoading: (loading: boolean) => void;
  /**
   * The chosen group's site list is in flight. Owned by effect (1b) only:
   * when the accounts loader also cleared it, React flushed (1b) between two
   * store writes and the bar read "0 sites" for the whole request.
   */
  sitesLoading: boolean;
  setSitesLoading: (loading: boolean) => void;
  /** Set when some or all accounts' site lists failed to load; the list is incomplete. */
  sitesError: string;
  setSitesError: (message: string) => void;

  siteDetail: SiteConnectionDetail | null;
  setSiteDetail: (detail: SiteConnectionDetail | null) => void;
  siteDetailLoading: boolean;
  setSiteDetailLoading: (loading: boolean) => void;

  smtpInstructions: SmtpInstructions | null;
  setSmtpInstructions: (value: SmtpInstructions | null) => void;
  /** Site ids under the scope that are SMTP sites (customer/names/?group_type=smtp). */
  smtpSiteIds: number[];
  setSmtpSiteIds: (ids: number[]) => void;
  /** Chosen group's sites with a WireGuard gateway; null = not known (old admin). */
  wireGuardGatewaySiteIds: number[] | null;
  setWireGuardGatewaySiteIds: (ids: number[] | null) => void;
  /** Groups with an SMTP site (group/?group_type=smtp); null until loaded or if it failed. */
  smtpAccountIds: number[] | null;
  setSmtpAccountIds: (ids: number[] | null) => void;

  tunnels: WireGuardTunnel[];
  setTunnels: (tunnels: WireGuardTunnel[]) => void;
  tunnelsLoading: boolean;
  setTunnelsLoading: (loading: boolean) => void;
  tunnelsError: string;
  setTunnelsError: (message: string) => void;

  telemetry: GapResult<Record<number, WireGuardPeerTelemetry>> | null;
  setTelemetry: (
    value: GapResult<Record<number, WireGuardPeerTelemetry>> | null
  ) => void;
  siteToSite: GapResult<SiteToSiteConnection[]> | null;
  setSiteToSite: (value: GapResult<SiteToSiteConnection[]> | null) => void;
  receivers: GapResult<AlarmReceiver[]> | null;
  setReceivers: (value: GapResult<AlarmReceiver[]> | null) => void;
  receiverTests: GapResult<ReceiverTestRecord[]> | null;
  setReceiverTests: (value: GapResult<ReceiverTestRecord[]> | null) => void;
  /** Alerts processed for the scope in the last hour (ENG-1902). */
  alertsReceived: GapResult<AlertsReceived> | null;
  setAlertsReceived: (value: GapResult<AlertsReceived> | null) => void;
  /** Per-camera last alarm for the one site in scope (ENG-1921). */
  cameraLastAlarms: GapResult<CameraLastAlarms> | null;
  setCameraLastAlarms: (value: GapResult<CameraLastAlarms> | null) => void;
  /** The latest per-camera site test run (ENG-2633), for the card and the report. */
  siteCameraTest: SiteCameraTestRun | null;
  setSiteCameraTest: (value: SiteCameraTestRun | null) => void;
  cameraLiveness: GapResult<CameraLiveness> | null;
  /**
   * The last `network-targets/` rows the host picker fetched: their
   * default_path flags tell the Default-path check what admin will say.
   */
  networkTargets: NetworkTarget[];
  setNetworkTargets: (value: NetworkTarget[]) => void;
  setCameraLiveness: (value: GapResult<CameraLiveness> | null) => void;
  /**
   * The one site's last camera-list refresh (`site_status/`, on prod
   * today): a recent successful saved login is all-clear evidence.
   */
  savedLoginStatus: { siteId: number; status: RefreshStatus | null } | null;
  setSavedLoginStatus: (
    value: { siteId: number; status: RefreshStatus | null } | null
  ) => void;
  /**
   * The one site's cameras with their addresses (`camera/site/`, on prod
   * today). Tunnel resolution and the host picker use these, so they work
   * where the liveness endpoint is not deployed.
   */
  siteCameras: SiteCamera[] | null;
  setSiteCameras: (value: SiteCamera[] | null) => void;
  /** Milestone sites: their recording servers (host picker targets). */
  recordingServers: RecordingServer[] | null;
  setRecordingServers: (value: RecordingServer[] | null) => void;
  /** What we changed on the one site in scope over the chosen window (ENG-1840). */
  siteChanges: GapResult<SiteChanges> | null;
  setSiteChanges: (value: GapResult<SiteChanges> | null) => void;
  /**
   * The changes card's look-back for one site (ENG-2244); another site, or
   * none, means the default 24 h.
   */
  siteChangesWindow: { siteId: number; window: ChangesWindow } | null;
  setSiteChangesWindow: (
    value: { siteId: number; window: ChangesWindow } | null
  ) => void;
  /** A window change is refetching; the previous answer stays on screen. */
  siteChangesRefreshing: boolean;
  setSiteChangesRefreshing: (value: boolean) => void;
  /** "Compare to when it last worked" for one site; `result` null while loading. */
  siteChangesCompare: {
    siteId: number;
    at: string;
    result: GapResult<SettingsCompare> | null;
  } | null;
  setSiteChangesCompare: (
    value: {
      siteId: number;
      at: string;
      result: GapResult<SettingsCompare> | null;
    } | null
  ) => void;

  findings: Finding[];
  setFindings: (findings: Finding[]) => void;
  lastCheckedAt: number | null;
  setLastCheckedAt: (at: number | null) => void;
  /** Bumped by "Run all checks" so every loader re-runs. */
  refreshToken: number;
  requestRefresh: () => void;

  // ---- UI state shared across tabs ---------------------------------------
  symptom: SymptomId | null;
  setSymptom: (symptom: SymptomId | null) => void;
  openAffected: Record<string, boolean>;
  toggleAffected: (findingId: string) => void;
  /** Site-to-Site rows whose 4-rung check is expanded. */
  openS2sChecks: Record<string, boolean>;
  setS2sCheckOpen: (connectionId: string, open: boolean) => void;
  /** Tunnel to preselect when the WireGuard debugger mounts. */
  pendingDebugTunnelId: number | null;
  setPendingDebugTunnelId: (tunnelId: number | null) => void;
  /**
   * "Check reachability" hand-off: what to prefill on the Connections tab.
   * Two slots so each form consumes (and clears) its own copy.
   */
  pendingProbe: ProbePrefill | null;
  pendingCredentialPrefill: ProbePrefill | null;
  requestProbe: (prefill: ProbePrefill) => void;
  clearPendingProbe: () => void;
  clearPendingCredentialPrefill: () => void;
  /** Connectivity analyzer history, newest first. Session-only. */
  probeHistory: PortCheckResponse[];
  pushProbeHistory: (entry: PortCheckResponse) => void;

  toast: string;
  setToast: (message: string) => void;

  reset: () => void;
}

const initialData = {
  sites: [] as TroubleshootingSite[],
  parentAccounts: [] as ParentAccount[],
  accountsLoading: false,
  sitesLoading: false,
  sitesError: "",
  siteDetail: null as SiteConnectionDetail | null,
  siteDetailLoading: false,
  smtpInstructions: null as SmtpInstructions | null,
  smtpSiteIds: [] as number[],
  wireGuardGatewaySiteIds: null as number[] | null,
  smtpAccountIds: null as number[] | null,
  tunnels: [] as WireGuardTunnel[],
  tunnelsLoading: false,
  tunnelsError: "",
  telemetry: null,
  siteToSite: null,
  receivers: null,
  receiverTests: null,
  alertsReceived: null,
  cameraLastAlarms: null,
  siteCameraTest: null as SiteCameraTestRun | null,
  cameraLiveness: null,
  networkTargets: [] as NetworkTarget[],
  savedLoginStatus: null,
  siteCameras: null as SiteCamera[] | null,
  recordingServers: null as RecordingServer[] | null,
  siteChanges: null,
  siteChangesWindow: null as { siteId: number; window: ChangesWindow } | null,
  siteChangesRefreshing: false,
  siteChangesCompare: null as {
    siteId: number;
    at: string;
    result: GapResult<SettingsCompare> | null;
  } | null,
  findings: [] as Finding[],
  lastCheckedAt: null as number | null,
  refreshToken: 0,
  symptom: null as SymptomId | null,
  openAffected: {} as Record<string, boolean>,
  openS2sChecks: {} as Record<string, boolean>,
  pendingDebugTunnelId: null as number | null,
  pendingProbe: null as ProbePrefill | null,
  pendingCredentialPrefill: null as ProbePrefill | null,
  probeHistory: [] as PortCheckResponse[],
  toast: "",
};

export interface TabScope {
  scope: ScopeMode;
  groupId: number | null;
  siteId: number | null;
}

const SCOPES_STORAGE_KEY = "troubleshooterScopes";
const TABS: TroubleshootingTab[] = [
  "diagnosis",
  "connections",
  "wireguard",
  "smtp-cameras",
  "alarm-receivers",
  "fisheye-dewarp",
];

interface PersistedScopes {
  tab: TroubleshootingTab;
  tabScopes: Partial<Record<TroubleshootingTab, TabScope>>;
}

const isTab = (value: unknown): value is TroubleshootingTab =>
  TABS.includes(value as TroubleshootingTab);

const isTabScope = (value: unknown): value is TabScope => {
  const scope = value as TabScope | null;
  return (
    scope != null &&
    ["all", "group", "site"].includes(scope.scope) &&
    (scope.groupId == null || typeof scope.groupId === "number") &&
    (scope.siteId == null || typeof scope.siteId === "number")
  );
};

/** Last visit's tab and per-tab selections, if they parse. */
const loadPersistedScopes = (): PersistedScopes | null => {
  const raw = safeGetItem<Partial<PersistedScopes>>(SCOPES_STORAGE_KEY);
  if (!raw || !isTab(raw.tab)) return null;
  const tabScopes: Partial<Record<TroubleshootingTab, TabScope>> = {};
  Object.entries(raw.tabScopes ?? {}).forEach(([tab, scope]) => {
    if (isTab(tab) && isTabScope(scope)) tabScopes[tab] = scope;
  });
  return { tab: raw.tab, tabScopes };
};

const persistScopes = (
  tab: TroubleshootingTab,
  tabScopes: Partial<Record<TroubleshootingTab, TabScope>>
) => {
  safeSetItem(SCOPES_STORAGE_KEY, { tab, tabScopes } as PersistedScopes);
};

const defaultScope = {
  tab: "diagnosis" as TroubleshootingTab,
  scope: "all" as ScopeMode,
  groupId: null as number | null,
  siteId: null as number | null,
  tabScopes: {} as Partial<Record<TroubleshootingTab, TabScope>>,
};

const persistedInitialScope = (): typeof defaultScope => {
  const persisted = loadPersistedScopes();
  if (!persisted) return defaultScope;
  const current = persisted.tabScopes[persisted.tab];
  return {
    tab: persisted.tab,
    scope: current?.scope ?? "all",
    groupId: current?.groupId ?? null,
    siteId: current?.siteId ?? null,
    tabScopes: persisted.tabScopes,
  };
};

const initialState = {
  ...defaultScope,
  wgSubTab: "peer-health" as WireGuardSubTab,
  sampleData: false,
  sampleScenario: DEFAULT_SAMPLE_SCENARIO,
  ...initialData,
};

/**
 * A scope change for the current tab: mirrored into that tab's remembered
 * selection and written to localStorage in the same update.
 */
const withTabScope = (
  state: {
    tab: TroubleshootingTab;
    tabScopes: TroubleshootingState["tabScopes"];
  } & TabScope,
  patch: Partial<TabScope> & { tab?: TroubleshootingTab }
) => {
  const tab = patch.tab ?? state.tab;
  const next: TabScope = {
    scope:
      patch.scope ??
      (patch.tab ? state.tabScopes[tab]?.scope ?? "all" : state.scope),
    groupId:
      patch.groupId !== undefined
        ? patch.groupId
        : patch.tab
        ? state.tabScopes[tab]?.groupId ?? null
        : state.groupId,
    siteId:
      patch.siteId !== undefined
        ? patch.siteId
        : patch.tab
        ? state.tabScopes[tab]?.siteId ?? null
        : state.siteId,
  };
  const tabScopes = { ...state.tabScopes, [tab]: next };
  persistScopes(tab, tabScopes);
  return { tab, ...next, tabScopes };
};

export const useTroubleshootingStore = create<TroubleshootingState>((set) => ({
  ...initialState,
  ...persistedInitialScope(),
  // Each tab keeps its own selection: the tabs offer different groups and
  // sites (groups with tunnels, groups with SMTP sites, …), so one filter for
  // all of them was often a choice the tab could not show. Coming back to a
  // tab restores what was chosen there. A tab opened for the first time
  // starts wide, except that a single account is not a choice and is chosen.
  setTab: (tab) =>
    set((state) => {
      if (state.tab === tab) return { tab };
      const remembered = state.tabScopes[tab];
      if (remembered) return withTabScope(state, { tab, ...remembered });
      const only =
        state.parentAccounts.length === 1 ? state.parentAccounts[0].id : null;
      return withTabScope(state, {
        tab,
        siteId: null,
        groupId: only,
        scope: (only == null ? "all" : "group") as ScopeMode,
      });
    }),
  setWgSubTab: (wgSubTab) => set({ wgSubTab }),
  setScope: (scope) => set((state) => withTabScope(state, { scope })),
  setGroupId: (groupId) => set((state) => withTabScope(state, { groupId })),
  setSiteId: (siteId) => set((state) => withTabScope(state, { siteId })),
  // Without a site the link keeps the group being worked in (a group is
  // required, so dropping it would bounce the user back to the gate).
  openTabForSite: (tab, siteId) =>
    set((state) => {
      if (siteId == null)
        return withTabScope(state, {
          tab,
          siteId: null,
          groupId: state.groupId,
          scope: (state.groupId == null ? "all" : "group") as ScopeMode,
        });
      return withTabScope(state, {
        tab,
        scope: "site" as ScopeMode,
        siteId,
        groupId:
          state.sites.find((site) => site.id === siteId)?.groupId ??
          state.groupId,
      });
    }),
  clearLoadedData: () => set({ ...initialData }),
  setSampleData: (sampleData) => set({ sampleData }),
  setSampleScenario: (sampleScenario) => set({ sampleScenario }),
  setSites: (sites) => set({ sites }),
  // A group is required before anything is checked, but one account is not a
  // choice — take it in the same update that stores the accounts, so the page
  // never flashes the "choose a group" gate on its way to the only answer.
  setParentAccounts: (parentAccounts) =>
    set((state) => {
      // A remembered group the user can no longer see is dropped, so a stale
      // selection cannot leave a tab on an empty site list.
      const known = new Set(parentAccounts.map((account) => account.id));
      if (
        state.groupId != null &&
        parentAccounts.length &&
        !known.has(state.groupId)
      )
        return {
          parentAccounts,
          ...withTabScope(state, { scope: "all", groupId: null, siteId: null }),
        };
      if (state.groupId == null && parentAccounts.length === 1)
        return {
          parentAccounts,
          ...withTabScope(state, {
            groupId: parentAccounts[0].id,
            // A site chosen before the accounts landed (a deep link) keeps its
            // scope; filling the group in must not widen it back out.
            scope: (state.siteId != null ? "site" : "group") as ScopeMode,
          }),
        };
      return { parentAccounts };
    }),
  setAccountsLoading: (accountsLoading) => set({ accountsLoading }),
  setSitesLoading: (sitesLoading) => set({ sitesLoading }),
  setSitesError: (sitesError) => set({ sitesError }),
  setSiteDetail: (siteDetail) => set({ siteDetail }),
  setSiteDetailLoading: (siteDetailLoading) => set({ siteDetailLoading }),
  setSmtpInstructions: (smtpInstructions) => set({ smtpInstructions }),
  setSmtpSiteIds: (smtpSiteIds) => set({ smtpSiteIds }),
  setWireGuardGatewaySiteIds: (wireGuardGatewaySiteIds) =>
    set({ wireGuardGatewaySiteIds }),
  setSmtpAccountIds: (smtpAccountIds) => set({ smtpAccountIds }),
  setTunnels: (tunnels) => set({ tunnels }),
  setTunnelsLoading: (tunnelsLoading) => set({ tunnelsLoading }),
  setTunnelsError: (tunnelsError) => set({ tunnelsError }),
  setTelemetry: (telemetry) => set({ telemetry }),
  setSiteToSite: (siteToSite) => set({ siteToSite }),
  setReceivers: (receivers) => set({ receivers }),
  setAlertsReceived: (alertsReceived) => set({ alertsReceived }),
  setCameraLastAlarms: (cameraLastAlarms) => set({ cameraLastAlarms }),
  setSiteCameraTest: (siteCameraTest) => set({ siteCameraTest }),
  setCameraLiveness: (cameraLiveness) => set({ cameraLiveness }),
  setNetworkTargets: (networkTargets) => set({ networkTargets }),
  setSavedLoginStatus: (savedLoginStatus) => set({ savedLoginStatus }),
  setSiteCameras: (siteCameras) => set({ siteCameras }),
  setRecordingServers: (recordingServers) => set({ recordingServers }),
  setSiteChanges: (siteChanges) => set({ siteChanges }),
  setSiteChangesWindow: (siteChangesWindow) => set({ siteChangesWindow }),
  setSiteChangesRefreshing: (siteChangesRefreshing) =>
    set({ siteChangesRefreshing }),
  setSiteChangesCompare: (siteChangesCompare) => set({ siteChangesCompare }),
  setReceiverTests: (receiverTests) => set({ receiverTests }),
  // Findings are rebuilt from scratch on every input change, so guard on
  // content: an identical rebuild must not re-render the page (or re-run any
  // effect that reads findings), otherwise an unstable input can loop.
  setFindings: (findings) =>
    set((state) =>
      JSON.stringify(state.findings) === JSON.stringify(findings)
        ? {}
        : { findings }
    ),
  setLastCheckedAt: (lastCheckedAt) => set({ lastCheckedAt }),
  requestRefresh: () =>
    set((state) => ({ refreshToken: state.refreshToken + 1 })),
  setSymptom: (symptom) => set({ symptom }),
  toggleAffected: (findingId) =>
    set((state) => ({
      openAffected: {
        ...state.openAffected,
        [findingId]: !state.openAffected[findingId],
      },
    })),
  setS2sCheckOpen: (connectionId, open) =>
    set((state) => ({
      openS2sChecks: { ...state.openS2sChecks, [connectionId]: open },
    })),
  setPendingDebugTunnelId: (pendingDebugTunnelId) =>
    set({ pendingDebugTunnelId }),
  requestProbe: (prefill) =>
    set({ pendingProbe: prefill, pendingCredentialPrefill: prefill }),
  clearPendingProbe: () => set({ pendingProbe: null }),
  clearPendingCredentialPrefill: () => set({ pendingCredentialPrefill: null }),
  pushProbeHistory: (entry) =>
    set((state) => ({
      probeHistory: [entry, ...state.probeHistory].slice(0, 8),
    })),
  setToast: (toast) => set({ toast }),
  reset: () => {
    try {
      localStorage.removeItem(SCOPES_STORAGE_KEY);
    } catch {
      // storage unavailable: nothing to clear
    }
    set({ ...initialState });
  },
}));
